import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AddToTopTenRequestI } from "@/api/routes/top-ten";
import { useApiClient } from "@/context/api-client";

import {
  TOP_TEN_DEFAULT_LIMIT,
  TopTenDataI,
  TopTenQueryKeys,
} from "../lib/data";
import { isTopTenSwitchedOff } from "../lib/errors";

/**
 * The caller's Top 10, and whether the feature is switched on. A switched-off
 * backend is an answer: no Top 10, nothing to show. Any other failure is an
 * error, and the Top 10 actions stay hidden: fail closed.
 */
export const useFetchTopTen = (enabled = true) => {
  const api = useApiClient();

  return useQuery({
    queryKey: TopTenQueryKeys.all(),
    queryFn: async (): Promise<TopTenDataI> => {
      try {
        const response = await api.topTen.getTopTen();

        return {
          isSwitchedOn: true,
          limit: response.data?.limit || TOP_TEN_DEFAULT_LIMIT,
          items: response.data?.items ?? [],
        };
      } catch (error) {
        if (isTopTenSwitchedOff(error)) {
          return {
            isSwitchedOn: false,
            limit: TOP_TEN_DEFAULT_LIMIT,
            items: [],
          };
        }

        throw error;
      }
    },
    enabled,
  });
};

interface TopTenTargetI {
  campaignId: string;
  accountId: string;
}

export const useAddToTopTen = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      campaignId,
      accountId,
      ...body
    }: TopTenTargetI & AddToTopTenRequestI) =>
      api.topTen.addToTopTen(campaignId, accountId, body),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: TopTenQueryKeys.all() }),
  });
};

export const useRemoveFromTopTen = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ campaignId, accountId }: TopTenTargetI) =>
      api.topTen.removeFromTopTen(campaignId, accountId),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: TopTenQueryKeys.all() }),
  });
};
